import type { AgendaPreview } from "@keepoinc/site-page-react-component";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { PreviewHint } from "../../contexts/PreviewSite";
import { useKeyPress } from "../../hooks/KeyPress";
import CloseIcon from "../../icons/Close";
import EyeIcon from "../../icons/Eye";
import { Site } from "../../model/site";
import { stopScroll } from "../../utils/modal";
import { PREVIEW_BREAKPOINT } from "../AppShell/styles";
import { MicroCircleContainer } from "../CircleContainer/styles";
import { LivePreview } from "../LivePreview";

// Keepo pink -> brand purple: stands out from the standard (purple) buttons and the PLUS green
const TAB_GRADIENT = "linear-gradient(180deg, #F7A2CD 0%, var(--color-brand-blue) 100%)";

/* Edge tab: glued to the right border, vertically centered, so it doesn't cover content */
const OpenTab = styled.button`
  position: fixed;
  top: 50%;
  right: 0;
  z-index: 95;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  padding: 0.75rem 0.3125rem;
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  font-weight: 800;
  color: var(--color-neutral-white);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: ${TAB_GRADIENT};
  border-radius: 12px 0 0 12px;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.35);
  transform: translateY(-50%);
  transition: padding-right 0.15s;

  &:hover {
    padding-right: 0.5rem;
  }

  span {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
  }

  @media (min-width: ${PREVIEW_BREAKPOINT}) {
    display: none;
  }
`;

/* Slides in from the right, over the editor */
const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  background: var(--color-neutral-darkness);
  box-shadow: -12px 0 32px rgba(0, 0, 0, 0.5);
  transform: translateX(${props => (props.$open ? "0" : "100%")});
  visibility: ${props => (props.$open ? "visible" : "hidden")};
  transition:
    transform 0.3s ease,
    visibility 0.3s;

  .header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: calc(12px + env(safe-area-inset-top)) 20px 12px;
    border-bottom: 1px solid var(--color-neutral-dark);
  }

  .body {
    display: flex;
    flex: 1;
    justify-content: center;
    min-height: 0;
  }
`;

type Props = {
  site: Site;
  hint?: PreviewHint;
  address?: string;
  /** A link to one of the Keepo's pages was clicked in the preview (see LivePreview). */
  onOpenPage?: (path: string) => void;
  /** The owner's own services and days on the agenda page (see LivePreview). */
  agendaPreview?: AgendaPreview;
};

/**
 * Preview for screens without the side preview (mobile and narrow desktop): an edge
 * tab opens the Keepo full screen on top of the editor. The editor stays mounted
 * underneath, so unsaved changes are kept (and shown, via the draft preview context).
 */
export const MobilePreview = ({ site, hint, address, onOpenPage, agendaPreview }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // The overlay is mounted on first open and then kept, so it can animate in and out
  const [mounted, setMounted] = useState(false);

  useKeyPress("Escape", () => setOpen(false));

  useEffect(() => (open ? stopScroll() : undefined), [open]);

  const openPreview = () => {
    setMounted(true);
    // Next frame, so the slide-in transition runs after the overlay is in the DOM
    requestAnimationFrame(() => setOpen(true));
  };

  return (
    <>
      <OpenTab type="button" onClick={openPreview} aria-label={t("components.mobilePreview.title")}>
        <EyeIcon width={14} height={12} color="currentColor" />
        <span>{t("components.mobilePreview.open")}</span>
      </OpenTab>

      {mounted && (
        <Overlay $open={open} role="dialog" aria-label={t("components.mobilePreview.title")} aria-hidden={!open}>
          <div className="header">
            <h4>{t("components.mobilePreview.title")}</h4>
            <MicroCircleContainer $color="var(--color-neutral-base)" cursor="pointer" onClick={() => setOpen(false)}>
              <CloseIcon height={9} width={9} color="var(--color-neutral-white)" />
            </MicroCircleContainer>
          </div>
          <div className="body">
            <LivePreview
              site={site}
              fullscreen
              hint={hint}
              address={address}
              onOpenPage={onOpenPage}
              agendaPreview={agendaPreview}
            />
          </div>
        </Overlay>
      )}
    </>
  );
};
